/**
 * Das Tor vor der Anwendung.
 *
 * Der Anmeldebildschirm ist **kein vierter Bildschirm** (ADR 0007): Er hat
 * keine Sidebar, keine Reiter, und nach der Anmeldung ist er nicht mehr
 * erreichbar. Deshalb ist er auch keine Route — dieser Provider entscheidet
 * vor dem Router, was gerendert wird. Wer den Login als Route baut, muss ihn
 * anschließend gegen jeden angemeldeten Zugriff verteidigen.
 *
 * Die Sitzung selbst verwaltet Supabase: Ablauf, Erneuerung, Wiederherstellen
 * nach einem Neuladen. Wir hören nur zu und geben das Token weiter.
 */

import type { AuthError, Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { AuthContext, discardSessionData, rememberSession } from '@/features/auth/auth-context'
import type { AuthState, AuthStatus } from '@/features/auth/auth-context'
import { LoginScreen } from '@/features/auth/LoginScreen'
import { setSignOutSource } from '@/lib/api'
import { supabase } from '@/lib/supabase'

/**
 * Die Meldungen des Anmeldedienstes sind englisch und technisch. Übersetzt
 * wird hier, weil nur diese Schicht Supabase kennt — das Formular zeigt an,
 * was es bekommt.
 *
 * Fehlermeldungen sagen, was schiefging und was zu tun ist, und entschuldigen
 * sich nicht (`design-system`).
 */
function messageFor(error: AuthError): string {
  switch (error.code) {
    case 'invalid_credentials':
      return 'E-Mail oder Passwort stimmt nicht.'
    case 'email_not_confirmed':
      return 'Diese Adresse ist noch nicht bestätigt. Öffne den Link aus der Einladung.'
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return 'Zu viele Versuche. Warte eine Minute und melde dich erneut an.'
    default:
      // Ohne Status ist die Anfrage gar nicht angekommen — das ist ein anderer
      // Rat als „Zugangsdaten prüfen".
      return error.status === undefined || error.status === 0
        ? 'Der Anmeldedienst ist nicht erreichbar. Prüfe die Verbindung.'
        : 'Die Anmeldung ist fehlgeschlagen. Versuche es erneut.'
  }
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<Session | null>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // `onAuthStateChange` meldet sich sofort mit der wiederhergestellten
    // Sitzung (`INITIAL_SESSION`) und danach bei jeder Erneuerung. Ein
    // zusätzliches `getSession()` wäre ein zweiter Weg zum selben Wert — und
    // zwei Wege bedeuten irgendwann zwei Wahrheiten.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      if (rememberSession(next?.access_token ?? null, next?.user.id ?? null)) {
        discardSessionData(queryClient)
      }
      setSession(next)
      setIsReady(true)
    })

    return () => {
      data.subscription.unsubscribe()
    }
  }, [queryClient])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error !== null) throw new Error(messageFor(error))
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  // Brücke für `lib/api.ts::request` (Spec 25, Fachregel 5): Ein 403
  // `user_disabled` ruft dieselbe Abmeldung, ohne dass die Transportschicht
  // Supabase kennen müsste.
  useEffect(() => {
    setSignOutSource(() => {
      void signOut()
    })
  }, [signOut])

  const status: AuthStatus = !isReady ? 'pending' : session === null ? 'anonymous' : 'authenticated'

  const value = useMemo<AuthState>(
    () => ({ status, email: session?.user.email ?? null, signIn, signOut }),
    [status, session, signIn, signOut],
  )

  return (
    <AuthContext.Provider value={value}>
      {status === 'pending' && <StartupBlank />}
      {status === 'anonymous' && <LoginScreen />}
      {session !== null && <Fragment key={session.user.id}>{children}</Fragment>}
    </AuthContext.Provider>
  )
}

/**
 * Die Millisekunden, in denen die gespeicherte Sitzung gelesen wird.
 *
 * Bewusst eine ruhige Fläche und kein Skeleton: Welche Zielform kommt, steht
 * hier noch nicht fest — ein Formular oder „Heute". Ein Skeleton, das sich als
 * das Falsche herausstellt, ist ein Sprung mehr als gar keiner.
 */
function StartupBlank() {
  return (
    <div data-testid="auth-pending" aria-busy="true" className="h-dvh">
      <span className="sr-only">Anmeldung wird geprüft</span>
    </div>
  )
}
